/**
 * FormsDslDocs — a searchable in-app reference + examples for the Forms DSL,
 * opened from the workbench toolbar (📖 Docs). Left: a grouped, filterable topic
 * list. Right: the selected topic rendered from its body blocks, with Copy /
 * Insert on every code example.
 *
 * Props: { open, onClose, onInsert? }  — onInsert(code) drops an example into the
 * editor at the cursor (omit to hide the Insert button).
 */
import { createSignal, createMemo, For, Show } from 'solid-js';
import FloatingPanel from './FloatingPanel.jsx';
import { TOPICS, DOC_GROUPS } from '../lib/formsdsl-docs.js';
import { toast } from '../lib/toast.js';

// Flatten a topic's searchable text once (title + keywords + prose + code).
const haystack = (t) => [
  t.title, t.keywords,
  ...t.body.map((b) => (typeof b === 'string' ? b : b.code || '')),
].join('\n').toLowerCase();

export default function FormsDslDocs(props) {
  const [query, setQuery] = createSignal('');
  const [activeId, setActiveId] = createSignal(TOPICS[0]?.id);

  const matches = createMemo(() => {
    const q = query().trim().toLowerCase();
    if (!q) return TOPICS;
    const words = q.split(/\s+/);
    return TOPICS.filter((t) => { const h = haystack(t); return words.every((w) => h.includes(w)); });
  });

  // Group the (filtered) topics for the left nav, preserving DOC_GROUPS order.
  const grouped = createMemo(() => {
    const inGroup = new Map();
    for (const t of matches()) {
      if (!inGroup.has(t.group)) inGroup.set(t.group, []);
      inGroup.get(t.group).push(t);
    }
    return DOC_GROUPS.filter((g) => inGroup.has(g)).map((g) => [g, inGroup.get(g)]);
  });

  // Keep a valid selection as the filter narrows.
  const active = createMemo(() => matches().find((t) => t.id === activeId()) || matches()[0] || null);

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };
  const insert = (code) => { props.onInsert?.(code); toast.success('Inserted into editor'); };

  // Inline markup inside a paragraph string: `code` (backticks) and **bold**.
  const inline = (text) => {
    const out = [];
    String(text).split('`').forEach((part, i) => {
      if (i % 2 === 1) { out.push(<code class="dslx-code">{part}</code>); return; }
      part.split('**').forEach((bp, j) => {
        if (bp === '') return;
        out.push(j % 2 === 1 ? <strong>{bp}</strong> : <span>{bp}</span>);
      });
    });
    return out;
  };

  // Render a topic body: strings are paragraphs ("## " heading, "- " bullet),
  // { code } blocks get Copy/Insert. Consecutive bullets group into one list.
  const renderBody = (body) => {
    const out = [];
    let bullets = null;
    const flush = () => { if (bullets) { out.push(<ul class="dslx-ul">{bullets}</ul>); bullets = null; } };
    for (const b of body) {
      if (typeof b !== 'string') {
        flush();
        out.push(
          <div class="dslx-codewrap">
            <div class="dslx-codebar">
              <Show when={props.onInsert}>
                <button class="btn btn-sm btn-ghost" onClick={() => insert(b.code)} title="Insert at cursor">⤵ Insert</button>
              </Show>
              <button class="btn btn-sm btn-ghost" onClick={() => copy(b.code)} title="Copy">⧉ Copy</button>
            </div>
            <pre class="dslx-pre"><code>{b.code}</code></pre>
          </div>,
        );
        continue;
      }
      if (b.startsWith('## ')) { flush(); out.push(<h4 class="dslx-h">{inline(b.slice(3))}</h4>); continue; }
      if (b.startsWith('- ')) { (bullets ||= []).push(<li>{inline(b.slice(2))}</li>); continue; }
      flush();
      out.push(<p class="dslx-p">{inline(b)}</p>);
    }
    flush();
    return out;
  };

  return (
    <FloatingPanel open={props.open} onClose={props.onClose}
                   title="📖 Forms DSL — documentation & examples" width={920} maxHeight="84vh">
      <div class="dslx">
        <div class="dslx-side">
          <input class="input dslx-search" placeholder="Search topics, properties, examples…" autofocus
                 value={query()} onInput={(e) => setQuery(e.currentTarget.value)} />
          <div class="dslx-nav">
            <Show when={grouped().length === 0}>
              <div class="muted" style="padding:10px;">No matches.</div>
            </Show>
            <For each={grouped()}>
              {([group, items]) => (
                <div class="dslx-group">
                  <div class="dslx-group-title">{group}</div>
                  <For each={items}>
                    {(t) => (
                      <button class="dslx-navitem" classList={{ active: active()?.id === t.id }}
                              onClick={() => setActiveId(t.id)}>
                        {t.title}
                      </button>
                    )}
                  </For>
                </div>
              )}
            </For>
          </div>
        </div>

        <div class="dslx-content">
          <Show when={active()} fallback={<div class="muted" style="padding:24px;">Pick a topic.</div>}>
            <div class="dslx-content-head">
              <span class="dslx-badge">{active().group}</span>
              <h3 class="dslx-title">{active().title}</h3>
            </div>
            <div class="dslx-body">{renderBody(active().body)}</div>
          </Show>
        </div>
      </div>
    </FloatingPanel>
  );
}
